import React, {useMemo} from "react";
import {CreateGeometryForPath} from "../../../../../Utils/ThreeJS/Paths/BasicPathUtils.js";
import {Vec2i} from "../../../../../Utils/ThreeJS/Paths/SharedPathUtils.js";
import {ColorArrayToTHREEColor} from "../../../../../Utils/ThreeJS/ColorUtils.js";
import {ObjPath} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../../Server/DB/maps/@Map.js";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {useBase64ImageStrAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {GetEntity} from "../../../../../Server/DB/entities.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";

/** Render a single mesh for one point-set of an ObjPath. Extracted from PathUI so a derived
 *  path (which produces multiple point-sets via extrude transforms) can render one PathMeshUI
 *  per point-set. The curve-type / tessellation / fBm-seeding logic is identical to the former
 *  inline PathUI implementation — only the point-set is parameterised. */
export const PathMeshUI = observer_mgl_r3((props: {
	path: ObjPath, obj: MapObject, map: DMap, pathIndex: number,
	/** The point-set to render for this mesh (already resolved, and with "isClosed" status pre-applied; one entry per PathMeshUI). */
	points: Vec2[], pointsClosed: boolean,
})=>{
	const {path, obj, map, pathIndex, points, pointsClosed} = props;

	const materialColor = useMemo(()=>ColorArrayToTHREEColor(path.color), [path.color]);
	const textureEntity = GetEntity(path.texture_id);
	const texture = useBase64ImageStrAsTexture(textureEntity?.image_embedded);

	const geometry = useMemo(()=>{
		// The curve is already tessellated upstream by the `interpolate` PathTransform (in
		// `ApplyPathTransformsToPoints`); `points` is the final dense curve. Just build the
		// width-extruded mesh directly. For closed paths, append the first point at the end so
		// the line wraps back.
		const pts = pointsClosed && points.length >= 2 ? [...points, points[0]] : points;
		const g = CreateGeometryForPath(pts.map(a=>new Vec2i(a.x, a.y)), path.width ?? 1, path.texture_size?.x ?? 1, path.texture_size?.y ?? 1, path.maxTurnPerArcSegment);
		if (g) {
			g.attributes.position.needsUpdate = true;
			g.attributes.uv.needsUpdate = true;
		}
		return g;
	}, [path, points, pointsClosed]);

	if (geometry == null) return null;

	const opacity = path.opacity ?? 1;
	return (
		<mesh position={Vec3.zero.NewZ(path.zOffset ?? 0).To3JPos()}>
			<primitive attach="geometry" object={geometry}/>
			<meshBasicMaterial attach="material" color={materialColor} map={texture} opacity={opacity}
				transparent={true}/>
		</mesh>
	);
});
